import React, { useEffect, useMemo, useRef, useState } from "react";
import { MondayApiQueryInstance } from "../../../../services/monday/monday-service-ts";
import logger from "../../../../lib/logger";
import { UserAvatar } from "../../../ui/UserAvatar";
import { cachedPeople, rememberPeople } from "../../columns/personCache";
import {
  buildPeopleValue,
  buildRatingValue,
  buildSelectionValue,
  buildStatusValue,
  PersonOrTeam,
} from "../buildColumnValue";
import { EDITOR_INPUT_CLASS, EditorFrame, EditorProps } from "./EditorFrame";
import { useBoardTags, usePeopleSearch } from "./usePickerData";

const OPTION_CLASS =
  "flex gap-2 items-center w-full px-2 py-1 text-left text-sm rounded bg-transparent border-0 cursor-pointer hover:bg-[#dcdfec] !font-figtree";

interface StatusLabel {
  index: number;
  name: string;
  color: string;
}

/** Status labels in board order, skipping the unnamed slots monday keeps in settings. */
export const getStatusLabels = (settings: any): StatusLabel[] => {
  const labels: Record<string, string> = settings?.labels ?? {};
  const positions: Record<string, number> = settings?.labels_positions_v2 ?? {};
  return Object.entries(labels)
    .filter(([, name]) => typeof name === "string" && name.trim() !== "")
    .map(([index, name]) => ({
      index: Number(index),
      name,
      color: settings?.labels_colors?.[index]?.color ?? "#c4c4c4",
    }))
    .sort((a, b) => (positions[a.index] ?? a.index) - (positions[b.index] ?? b.index));
};

/** The hint under a picker, above a divider, saying how it saves. */
const PickerFooter = ({ children, hint }: { children?: React.ReactNode; hint: string }) => (
  <div className="flex justify-between items-center pt-2 w-full border-0 border-t border-solid border-[#e6e9ef]">
    {children ?? <span />}
    <span className="text-xs text-light-secondary-text-color">{hint}</span>
  </div>
);

/** Status: a grid of label chips, like monday's label picker. One click saves. */
export const StatusEditor = ({ state, onCommit, onCancel }: EditorProps) => (
  <EditorFrame
    build={() => buildStatusValue(null)}
    dirty={false}
    onCommit={onCommit}
    onCancel={onCancel}
  >
    <div className="grid grid-cols-2 gap-1.5 w-full" role="listbox" aria-label="Status labels">
      {getStatusLabels(state.settings).map((label, position) => {
        const selected = state.value?.index === label.index;
        return (
          <button
            key={label.index}
            type="button"
            role="option"
            aria-selected={selected}
            autoFocus={position === 0}
            title={label.name}
            className="overflow-hidden px-2 h-8 text-[13px] font-semibold text-white whitespace-nowrap rounded border-0 cursor-pointer text-ellipsis hover:opacity-90 !font-figtree"
            style={{
              backgroundColor: label.color,
              boxShadow: selected ? "0 0 0 2px #ffffff, 0 0 0 4px #323338" : undefined,
            }}
            onClick={() =>
              onCommit(
                buildStatusValue(label.index),
                <span
                  className="px-3 py-1 font-semibold text-white rounded"
                  style={{ backgroundColor: label.color }}
                >
                  {label.name}
                </span>,
              )
            }
          >
            {label.name}
          </button>
        );
      })}
    </div>
    <PickerFooter hint="One click saves">
      <button
        type="button"
        className="px-1.5 py-1 text-[13px] bg-transparent rounded border-0 cursor-pointer hover:bg-[#dcdfec] !font-figtree"
        onClick={() => onCommit(buildStatusValue(null))}
      >
        Clear
      </button>
    </PickerFooter>
  </EditorFrame>
);

/** Dropdown labels; settings come as `[{id, name}]` today and as an id→name map on old boards. */
export const getDropdownLabels = (settings: any): { id: number; name: string }[] => {
  const labels = settings?.labels;
  if (Array.isArray(labels))
    return labels.map((label) => ({ id: Number(label.id), name: label.name }));
  if (labels && typeof labels === "object") {
    return Object.entries(labels).map(([id, name]) => ({ id: Number(id), name: String(name) }));
  }
  return [];
};

interface Option {
  id: number;
  name: string;
  /** People and teams show their picture, or their initials when they have none. */
  avatar?: { photoUrl?: string };
  /** Still being looked up: drawn as a placeholder instead of a stand-in name. */
  loading?: boolean;
  /** The name is a stand-in ("Team 5"), not what the column will show. */
  standIn?: boolean;
}

const matches = (name: string, search: string) =>
  name.toLowerCase().includes(search.trim().toLowerCase());

const sameIds = (a: number[], b: number[]) =>
  a.length === b.length && [...a].sort().every((id, i) => id === [...b].sort()[i]);

interface MultiSelectProps {
  options: Option[];
  initial: number[];
  emptyText: string;
  build: (ids: number[]) => ReturnType<typeof buildPeopleValue>;
  onCommit: EditorProps["onCommit"];
  onCancel: EditorProps["onCancel"];
  header?: React.ReactNode;
  footer?: React.ReactNode;
  /** Ids to select as they appear, e.g. a tag just created from the picker. */
  added?: number[];
  /** The choices are still loading: show skeleton rows in the list. */
  loading?: "plain" | "avatars";
  /** Draws the chosen entries as the column shows them, to stand in for the value while it saves. */
  preview: (chosen: Option[]) => React.ReactNode;
}

const RemoveIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#676879"
    strokeWidth="2.5"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/** Grey rows standing in for a picker's choices while they load. */
export const OptionSkeleton = ({ avatars }: { avatars?: boolean }) => (
  <div aria-label="Loading" className="flex flex-col gap-1 w-full">
    {[72, 56, 64].map((width) => (
      <div key={width} className="flex gap-2 items-center px-2 h-8">
        {avatars && <span className="w-6 h-6 rounded-full animate-pulse bg-[#e6e9ef]" />}
        <span className="h-3 rounded animate-pulse bg-[#e6e9ef]" style={{ width: `${width}%` }} />
      </div>
    ))}
  </div>
);

const EmptyPreview = () => <span className="text-light-secondary-text-color">Add value</span>;

/**
 * Picker buttons keep focus where it is (the search box): a pressed button that then removes
 * itself, as a picked entry or a chip's × does, would otherwise drop focus to the page and the
 * editor would stop hearing click-away, Enter and Escape.
 */
const keepFocus = (event: React.MouseEvent) => event.preventDefault();

/** Before a focused button removes itself (keyboard use), hand focus to the search box or frame. */
const handOffFocus = (button: HTMLElement) => {
  if (document.activeElement !== button) return;
  const frame = button.closest<HTMLElement>('[tabindex="-1"]');
  (frame?.querySelector<HTMLElement>("input") ?? frame)?.focus();
};

/** Enter on a focused picker button picks or removes, rather than saving the whole editor. */
const pressOnEnter = (event: React.KeyboardEvent<HTMLButtonElement>) => {
  if (event.key !== "Enter") return;
  event.preventDefault();
  event.stopPropagation();
  event.currentTarget.click();
};

/**
 * monday's multi-picker: what's selected sits on top as chips (× removes one), and clicking an
 * entry in the list below adds it. Saves when focus leaves it, or on Enter.
 */
const MultiSelect = ({
  options,
  initial,
  emptyText,
  build,
  onCommit,
  onCancel,
  header,
  footer,
  added = [],
  loading,
  preview,
}: MultiSelectProps) => {
  const [selected, setSelected] = useState<number[]>(initial);
  // Chips keep their label and picture while a search hides their entry from the list.
  const seen = useRef<Record<number, Option>>({});
  options.forEach((option) => (seen.current[option.id] = option));

  useEffect(() => {
    setSelected((prev) => [...prev, ...added.filter((id) => !prev.includes(id))]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [added.join(",")]);

  const add = (id: number) => setSelected((prev) => (prev.includes(id) ? prev : [...prev, id]));
  const remove = (id: number) => setSelected((prev) => prev.filter((x) => x !== id));
  const available = options.filter((option) => !selected.includes(option.id));

  // Shows the new value while it saves, unless a name is still unknown: then the old one stays.
  const commitWithPreview: EditorProps["onCommit"] = (result) => {
    const chosen = selected.map((id) => seen.current[id]);
    const known = chosen.every((option) => option && !option.loading && !option.standIn);
    onCommit(result, !known ? undefined : chosen.length > 0 ? preview(chosen) : <EmptyPreview />);
  };

  return (
    <EditorFrame
      build={() => build(selected)}
      dirty={!sameIds(selected, initial)}
      onCommit={commitWithPreview}
      onCancel={onCancel}
    >
      {selected.length > 0 && (
        <ul className="flex flex-wrap gap-1 p-0 m-0 w-full list-none" aria-label="Selected">
          {selected.map((id) => {
            const option = seen.current[id] ?? { id, name: String(id) };
            if (option.loading) {
              return (
                <li
                  key={id}
                  aria-label="Loading"
                  className="w-24 h-6 rounded-full animate-pulse bg-[#e6e9ef]"
                />
              );
            }
            return (
              <li
                key={id}
                className="flex gap-1 items-center py-0.5 pr-1 pl-2 max-w-full text-[13px] rounded-full bg-[#e6e9ef]"
              >
                {option.avatar && (
                  <UserAvatar name={option.name} photoUrl={option.avatar.photoUrl} size="sm" />
                )}
                <span className="overflow-hidden whitespace-nowrap text-ellipsis">
                  {option.name}
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${option.name}`}
                  className="flex justify-center items-center p-0 w-4 h-4 bg-transparent rounded-full border-0 cursor-pointer hover:bg-[#c3c6d4]"
                  onMouseDown={keepFocus}
                  onKeyDown={pressOnEnter}
                  onClick={(event) => {
                    handOffFocus(event.currentTarget);
                    remove(id);
                  }}
                >
                  <RemoveIcon />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {header}
      <div
        role="listbox"
        aria-multiselectable="true"
        className="flex flex-col gap-0.5 w-full max-h-[220px] overflow-y-auto"
      >
        {loading && <OptionSkeleton avatars={loading === "avatars"} />}
        {!loading && available.length === 0 && (options.length > 0 || emptyText) && (
          <p className="px-2 text-sm text-light-secondary-text-color">
            {options.length > 0 ? "Everything here is selected." : emptyText}
          </p>
        )}
        {available.map((option, position) => (
          <button
            key={option.id}
            type="button"
            role="option"
            aria-selected={false}
            autoFocus={!header && position === 0}
            className={OPTION_CLASS}
            onMouseDown={keepFocus}
            onKeyDown={pressOnEnter}
            onClick={(event) => {
              handOffFocus(event.currentTarget);
              add(option.id);
            }}
          >
            {option.avatar && (
              <UserAvatar name={option.name} photoUrl={option.avatar.photoUrl} size="sm" />
            )}
            {option.name}
          </button>
        ))}
        {footer}
      </div>
      <PickerFooter hint="Click away to save · Esc cancels" />
    </EditorFrame>
  );
};

export const DropdownEditor = ({ state, onCommit, onCancel }: EditorProps) => {
  const [search, setSearch] = useState("");
  const options = getDropdownLabels(state.settings);
  // Only ids the column still has: monday stores an unknown dropdown id without complaint.
  const known = new Set(options.map((option) => option.id));
  const initial = ((state.value?.ids ?? []) as number[]).map(Number).filter((id) => known.has(id));

  return (
    <MultiSelect
      options={options.filter((option) => matches(option.name, search))}
      initial={initial}
      emptyText={search.trim() ? "No labels match." : "This column has no labels yet."}
      build={(ids) => buildSelectionValue("dropdown", ids)}
      preview={(chosen) => (
        <div className="flex flex-wrap gap-1">
          {chosen.map((label) => (
            <span key={label.id} className="px-2 py-0.5 text-sm rounded bg-[#cce5ff]">
              {label.name}
            </span>
          ))}
        </div>
      )}
      onCommit={onCommit}
      onCancel={onCancel}
      header={
        <input
          autoFocus
          aria-label="Search labels"
          placeholder="Search labels"
          className={EDITOR_INPUT_CLASS}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      }
    />
  );
};

/** A failed lookup inside a picker: says so and offers a retry, instead of closing or looking empty. */
const LoadError = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
  <div role="alert" className="flex gap-2 items-center px-2 text-sm text-[#d83a52]">
    {message}
    <button
      type="button"
      className="p-0 text-sm bg-transparent border-0 cursor-pointer text-[#0060b9] hover:underline !font-figtree"
      onClick={onRetry}
    >
      Try again
    </button>
  </div>
);

export const TagsEditor = ({ state, boardId, onCommit, onCancel }: EditorProps) => {
  const { data: tags, error: loadError, mutate: mutateTags } = useBoardTags(boardId);
  const [search, setSearch] = useState("");
  const [created, setCreated] = useState<number[]>([]);
  const [createFailed, setCreateFailed] = useState(false);
  const initial = ((state.value?.tag_ids ?? []) as number[]).map(Number);

  if (loadError && !tags) {
    return (
      <EditorFrame
        build={() => buildSelectionValue("tags", initial)}
        dirty={false}
        onCommit={onCommit}
        onCancel={onCancel}
      >
        <LoadError message="Couldn't load this board's tags." onRetry={() => mutateTags()} />
      </EditorFrame>
    );
  }

  const listed: Option[] = (tags ?? []).map((tag) => ({ id: Number(tag.id), name: tag.name }));
  // Until the board's tags arrive, the item's own show as placeholders. After, any the listing
  // doesn't include are kept under their id.
  const missing: Option[] = initial
    .filter((id) => !listed.some((tag) => tag.id === id))
    .map((id) =>
      tags ? { id, name: `Tag ${id}`, standIn: true } : { id, name: "", loading: true },
    );
  const options = [...listed, ...missing];

  const term = search.trim();
  const exists = options.some((tag) => tag.name.toLowerCase() === term.toLowerCase());

  // monday creates tags as you type one in; create_or_get_tag returns the existing tag if the
  // account already has one by that name.
  const createTag = () => {
    setCreateFailed(false);
    MondayApiQueryInstance.createOrGetTag(boardId, term)
      .then((tag) => {
        const id = Number(tag.id);
        void mutateTags(
          (current) =>
            current?.some((t) => Number(t.id) === id) ? current : [...(current ?? []), tag],
          { revalidate: false },
        );
        setCreated((prev) => [...prev, id]);
        setSearch("");
      })
      .catch((error) => {
        logger.error("Error creating tag", error);
        setCreateFailed(true);
      });
  };

  return (
    <MultiSelect
      options={options.filter((tag) => tag.loading || matches(tag.name, term))}
      initial={initial}
      added={created}
      loading={tags ? undefined : "plain"}
      emptyText={term ? "" : "This board has no tags yet. Type one to create it."}
      build={(ids) => buildSelectionValue("tags", ids)}
      preview={(chosen) => <div>{chosen.map((tag) => tag.name).join(", ")}</div>}
      onCommit={onCommit}
      onCancel={onCancel}
      header={
        <input
          autoFocus
          aria-label="Search or create tags"
          placeholder="Search or create a tag"
          className={EDITOR_INPUT_CLASS}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            // Enter on a new name creates the tag instead of saving the column.
            if (event.key === "Enter" && term && !exists && tags) {
              event.preventDefault();
              event.stopPropagation();
              createTag();
            }
          }}
        />
      }
      footer={
        <>
          {tags && term && !exists && (
            <button
              type="button"
              className={OPTION_CLASS}
              onMouseDown={keepFocus}
              onKeyDown={pressOnEnter}
              onClick={createTag}
            >
              + Create tag “{term}”
            </button>
          )}
          {createFailed && <LoadError message="Couldn't create the tag." onRetry={createTag} />}
        </>
      }
    />
  );
};

/** Users and teams share one id space in the picker, so a team's key is its id negated. */
const toKey = (entry: PersonOrTeam) => (entry.kind === "team" ? -entry.id : entry.id);
const fromKey = (key: number): PersonOrTeam =>
  key < 0 ? { id: -key, kind: "team" } : { id: key, kind: "person" };

type Person = Option & { avatar: { photoUrl?: string } };

const userOption = (user: { id: string; name: string; photo_thumb_small?: string }): Person => {
  // So the column can draw whoever is picked as soon as the edit saves.
  rememberPeople([{ id: user.id, name: user.name, photo_thumb: user.photo_thumb_small }]);
  return { id: Number(user.id), name: user.name, avatar: { photoUrl: user.photo_thumb_small } };
};

export const PeopleEditor = ({ state, onCommit, onCancel }: EditorProps) => {
  const initialKeys: number[] = (state.value?.personsAndTeams ?? []).map((entry: PersonOrTeam) =>
    toKey({ id: Number(entry.id), kind: entry.kind }),
  );
  const [search, setSearch] = useState("");
  // The search waits for typing to pause; the first one (everyone) runs at once.
  const [term, setTerm] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setTerm(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  const { data: users, error: searchError, mutate: retrySearch } = usePeopleSearch(term);
  const results = useMemo(() => users?.map(userOption), [users]);
  const searchFailed = Boolean(searchError) && !users;
  const selectedUserIds = initialKeys.filter((key) => key > 0);
  // Everyone seen so far, so the people already selected keep their name and picture. Starts
  // from the people the column has already drawn, so they show at once without a lookup.
  const [known, setKnown] = useState<Record<number, Person>>(() =>
    Object.fromEntries(
      selectedUserIds.flatMap((id) => {
        const person = cachedPeople([id])?.[0];
        return person
          ? [[id, { id, name: person.name, avatar: { photoUrl: person.photo_thumb } }]]
          : [];
      }),
    ),
  );
  const [selectedLoaded, setSelectedLoaded] = useState(false);

  const remember = (people: Person[]) =>
    setKnown((prev) => ({ ...prev, ...Object.fromEntries(people.map((p) => [p.id, p])) }));

  useEffect(() => {
    const unknown = selectedUserIds.filter((id) => !known[id]);
    if (unknown.length === 0) {
      setSelectedLoaded(true);
      return;
    }
    MondayApiQueryInstance.getUsersByIds(unknown)
      .then((users) => remember(users.map(userOption)))
      .catch((error) => logger.error("Error loading selected people", error))
      .finally(() => setSelectedLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Selected people stay listed while a search narrows the results.
  const options: Option[] = [
    ...initialKeys
      .filter((key) => !results?.some((o) => o.id === key))
      .map(
        (key) =>
          known[key] ?? {
            id: key,
            name: key < 0 ? `Team ${-key}` : `User ${key}`,
            standIn: true,
            avatar: {},
            // "User 123" only if the lookup failed; until it answers, a placeholder.
            loading: key > 0 && !selectedLoaded,
          },
      ),
    ...(results ?? []),
  ];

  return (
    <MultiSelect
      options={options}
      initial={initialKeys}
      loading={results || searchFailed ? undefined : "avatars"}
      emptyText={searchFailed ? "" : "No one matches."}
      build={(keys) => buildPeopleValue(keys.map(fromKey))}
      preview={(chosen) => (
        <div className="flex font-semibold">
          {chosen.slice(0, 3).map((person) => (
            <UserAvatar
              key={person.id}
              name={person.name}
              photoUrl={person.avatar?.photoUrl}
              className="-mx-1 border-2 border-[#f5f6f8]"
            />
          ))}
          {chosen.length > 3 && (
            <span className="flex justify-center items-center px-2 -mx-1 text-xs rounded-full bg-[#dcdfec] text-[#676879]">
              +{chosen.length - 3}
            </span>
          )}
        </div>
      )}
      onCommit={onCommit}
      onCancel={onCancel}
      header={
        <input
          autoFocus
          aria-label="Search people"
          placeholder="Search names"
          className={EDITOR_INPUT_CLASS}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      }
      footer={
        searchFailed && (
          <LoadError message="Couldn't search people." onRetry={() => retrySearch()} />
        )
      }
    />
  );
};

/** Rating: click a star to save; clicking the current rating clears it. */
export const RatingEditor = ({ state, onCommit, onCancel }: EditorProps) => {
  const max = Number(state.settings?.max) || 5;
  const current = Number(state.value?.rating) || 0;

  return (
    <EditorFrame
      build={() => buildRatingValue(current, max)}
      dirty={false}
      onCommit={onCommit}
      onCancel={onCancel}
    >
      <div className="flex gap-1" role="radiogroup" aria-label="Rating">
        {Array.from({ length: max }, (_, i) => i + 1).map((star) => (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={star === current}
            aria-label={`${star} of ${max}`}
            autoFocus={star === 1}
            className="text-xl leading-none bg-transparent border-0 cursor-pointer p-0"
            style={{ color: star <= current ? "#ffcb00" : "#c3c6d4" }}
            onClick={() => onCommit(buildRatingValue(star === current ? 0 : star, max))}
          >
            ★
          </button>
        ))}
      </div>
    </EditorFrame>
  );
};
